import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";

import { FiSearch } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import LoaderMini from "../Loader/LoaderMini";
import { fetchSearchResults } from "../../redux/slices/searchSlice";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SearchPopup = ({ setShowSearchPopup }) => {
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { results, loading } = useSelector((state) => state.search);
  const inputRef = useRef();
  const { catalogs } = useSelector((state) => state.catalogs);
  const searchResultsRef = useRef(null);
  const closeButtonRef = useRef(null);
  const { i18n, t } = useTranslation();
  const isEng = i18n.language === "en";
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.length >= 3) {
        dispatch(fetchSearchResults(query));
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query, dispatch]);

  const handleProductClick = (productId) => {
    setShowDropdown(false);
    setShowSearchPopup(false);
    navigate(`/products/${productId}`);
  };

  const handleCategoryClick = (categoryId) => {
    setShowDropdown(false);
    setShowSearchPopup(false);
    navigate(`/categories/${categoryId}`);
  };
  const handleOccasionClick = (occasionId) => {
    setShowDropdown(false);
    setShowSearchPopup(false);
    navigate(`/catalogs/${occasionId}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.length >= 3) {
      dispatch(fetchSearchResults(query));
      navigate(`/search?term=${query}`);
      setShowDropdown(false);
      setShowSearchPopup(false);
    }
  };

  const highlightMatch = (text) => {
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} className="text-purble-2 font-bold">
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !inputRef.current?.contains(e.target) &&
        searchResultsRef.current &&
        !searchResultsRef.current.contains(e.target) &&
        closeButtonRef.current &&
        !closeButtonRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  console.log(results);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
      }}
      layout="true"
      className={`flex items-center justify-center w-full fixed top-0
      bg-white border-b h-[10.1rem] z-50`}
    >
      <motion.div
        className={`flex items-center justify-center w-full 
      bg-white `}
      >
        <div
          className="flex items-center justify-between w-full h-full py-5 
      relative "
        >
          <form onSubmit={handleSearchSubmit} className="overflow-hidden px-10">
            <div
              className="relative border border-blue-2 rounded-full flex 
          items-center justify-start overflow-hidden"
            >
              <input
                ref={inputRef}
                type="text"
                placeholder="Search products..."
                value={query}
                onFocus={() => {
                  if (query.length >= 3 && results.length > 0) {
                    setShowDropdown(true);
                  }
                }}
                onChange={(e) => setQuery(e.target.value)}
                className="w-[20rem] rounded-full h-[58px] focus:outline-none px-6
            "
              />
              <div
                className={`flex items-center justify-center absolute
            ${isEng ? "right-5" : "left-5"}`}
              >
                <button type="submit">
                  <FiSearch size={24} />
                </button>
              </div>
            </div>
          </form>
          <button
            type="button"
            className={`${isEng ? "mr-7" : "ml-7"}`}
            onClick={() => setShowSearchPopup(false)}
          >
            <IoClose size={24} />
          </button>
          {showDropdown && results.length > 0 ? (
            <div className="absolute z-50 top-full w-screen h-screen bg-black/50 flex flex-col ">
              <div
                ref={searchResultsRef}
                className=" w-full bg-white shadow-md border 
                 max-h-[30rem] lg:max-h-[30rem] px-5 
                 flex flex-col py-5"
              >
                <div
                  className=" flex flex-col w-full max-h-[30rem] lg:max-h-[27rem]
            relative"
                >
                  <button
                    type="button"
                    ref={closeButtonRef}
                    className={`absolute top-0 ${isEng ? "right-3" : "left-3"}`}
                    onClick={() => setShowDropdown(false)}
                  >
                    <IoClose size={24} />
                  </button>
                  {loading ? (
                    <div className="flex items-center justify-center py-5 ">
                      <LoaderMini color="bg-purble-2" />
                    </div>
                  ) : (
                    <div className="flex flex-col items-start justify-start gap-2">
                      <div
                        className="flex items-start justify-start gap-2 flex-col
                  w-full"
                      >
                        <div className="flex items-center justify-start gap-2">
                          <p>{t("searchResultsFor")}</p>
                          <p>`{highlightMatch(query)}`</p>
                        </div>
                        <button
                          onClick={handleSearchSubmit}
                          className={`hover:bg-gray-100 w-full flex items-start
                      rounded ${isEng ? "pl-6" : "pr-6"} gap-2`}
                        >
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center border
                    border-blue-2"
                          >
                            <FiSearch size={12} />
                          </div>
                          <p>{highlightMatch(query)}</p>
                        </button>
                      </div>
                      <div
                        className="flex items-start justify-start gap-2 flex-col
                  w-full"
                      >
                        <p className="font-bold">{t("resultsInCategories")}</p>
                      </div>
                      <div
                        className="flex flex-col gap-1 items-start justify-start
                w-full lg:w-[50%] max-h-[9rem] overflow-y-scroll "
                      >
                        {results.map((item) => (
                          <div
                            key={item.id}
                            className={` flex w-full 
                      items-center juatify-start rounded-lg gap-3
                      ${isEng ? "pl-6" : "pr-6"}`}
                          >
                            <div
                              onClick={() => handleProductClick(item?.id)}
                              className={`flex items-center justify-start gap-2 cursor-pointer`}
                            >
                              <div>
                                <img
                                  src={item?.website_image}
                                  alt={
                                    isEng
                                      ? item?.arabic_name
                                      : item?.english_name
                                  }
                                  className={`w-5 h-5 rounded-full`}
                                />
                              </div>
                              <p className="hover:underline underline-offset-1">
                                {highlightMatch(
                                  isEng ? item?.english_name : item?.arabic_name
                                )}
                              </p>
                            </div>
                            <p>{t("in")}</p>
                            <div
                              onClick={() =>
                                handleCategoryClick(item?.category_id)
                              }
                              className="text-sm text-gray-600 hover:underline
                        underline-offset-1 cursor-pointer"
                            >
                              {highlightMatch(
                                isEng
                                  ? item?.category_en_name
                                  : item?.category_ar_name
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div
                        className="flex items-start justify-start gap-2 flex-col
                  w-full"
                      >
                        <p className="font-bold">{t("resultsInOccasions")}</p>
                      </div>
                      <div
                        className="flex flex-col gap-1 items-start justify-start
                w-full lg:w-[50%] max-h-[10rem] lg:max-h-[9rem] overflow-y-scroll"
                      >
                        {results.map((item) => (
                          <div key={`${item?.id}-${item?.category_id}`}>
                            {item?.catalog_ids?.length > 0 &&
                              item?.catalog_ids.map((catalogId) => {
                                const matchedCatalog = catalogs?.find(
                                  (c) => c.id === catalogId
                                );
                                return (
                                  <div
                                    key={`${item?.id}-${catalogId}`}
                                    className={` flex w-full 
                              items-center juatify-start rounded-lg gap-3
                              ${isEng ? "pl-6" : "pr-6"}`}
                                  >
                                    <div
                                      onClick={() =>
                                        handleProductClick(item?.id)
                                      }
                                      className={`flex items-center justify-start gap-2 cursor-pointer`}
                                    >
                                      <div>
                                        <img
                                          src={item?.website_image}
                                          alt={
                                            isEng
                                              ? item?.arabic_name
                                              : item?.english_name
                                          }
                                          className={`w-5 h-5 rounded-full`}
                                        />
                                      </div>
                                      <p className="hover:underline underline-offset-1">
                                        {highlightMatch(
                                          isEng
                                            ? item?.english_name
                                            : item?.arabic_name
                                        )}
                                      </p>
                                    </div>
                                    <p>{t("in")}</p>
                                    <div
                                      className="text-sm text-gray-600  flex items-center
                            justify-start"
                                    >
                                      {matchedCatalog && (
                                        <div className="flex items-center justify-start gap-2">
                                          <div
                                            onClick={() =>
                                              handleOccasionClick(catalogId)
                                            }
                                            className="hover:underline cursor-pointer"
                                          >
                                            {highlightMatch(
                                              isEng
                                                ? matchedCatalog?.english_name
                                                : matchedCatalog?.arabic_name
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            results.length === 0 &&
            query.length >= 3 && (
              <div className="absolute z-50 top-full w-screen h-screen bg-black/50 flex flex-col ">
                <div className="flex items-center justify-center w-full h-[20rem] gap-2 bg-white">
                  <p>{t("noSearchResultsFor")}</p>
                  <p>`{highlightMatch(query)}`</p>
                </div>
              </div>
            )
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
export default SearchPopup;
